import React, { useState } from 'react';
import {
  MapPin,
  Navigation,
  Clock,
  Star,
  Search,
  ChevronRight,
  Wine,
  UtensilsCrossed,
  Lock,
  Check,
  X,
  Loader2,
  AlertCircle,
  Map,
  List,
  Compass,
} from 'lucide-react';
import {
  Lounge,
  getDistanceFromLatLng,
  isOpenNow,
} from '../data/lounges';
import GoogleMap from '../components/GoogleMap';

interface LoungeFinderViewProps {
  lounges: Lounge[];
  userLocation: { lat: number; lng: number } | null;
  locationLoading?: boolean;
  locationError?: string | null;
  searchRadius: number; // in miles
  onSelectLounge: (lounge: Lounge) => void;
  onRequestLocation: () => void;
  onRadiusChange: (radius: number) => void;
  onCitySearch: (city: string) => void;
  onNavigate?: (view: string) => void;
}

type SortOption = 'distance' | 'rating' | 'name';
type FilterOption = 'all' | 'open' | 'fullBar' | 'food' | 'lockers';

const RADIUS_OPTIONS = [5, 10, 25, 50, 100]; // miles

const LoungeFinderView: React.FC<LoungeFinderViewProps> = ({
  lounges,
  userLocation,
  locationLoading = false,
  locationError = null,
  searchRadius,
  onSelectLounge,
  onRequestLocation,
  onRadiusChange,
  onCitySearch,
  onNavigate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('distance');
  const [activeFilter, setActiveFilter] = useState<FilterOption>('all');
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [selectedMapLounge, setSelectedMapLounge] = useState<string | undefined>();

  // Handle search submission - always trigger geocoding search on Enter
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query) {
      // Always trigger geocoding search when user presses Enter
      // This handles city names, zip codes, addresses, etc.
      onCitySearch(query);
      setSearchQuery(''); // Clear after search so it doesn't filter results
    }
  };

  // Check if query looks like a location search (for showing Search button)
  const isLocationSearch = (query: string) => {
    const trimmed = query.trim();
    // Show search button for: city names, "city, state", zip codes, or any 3+ char query
    return trimmed.length >= 3;
  };

  // Calculate distances and sort lounges
  const loungesWithDistance = lounges.map((lounge) => ({
    ...lounge,
    distance: userLocation
      ? getDistanceFromLatLng(
          userLocation.lat,
          userLocation.lng,
          lounge.location.lat,
          lounge.location.lng
        )
      : null,
  }));

  // Filter lounges
  const filteredLounges = loungesWithDistance.filter((lounge) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        lounge.name.toLowerCase().includes(query) ||
        lounge.city.toLowerCase().includes(query) ||
        lounge.state.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Amenity filters
    switch (activeFilter) {
      case 'open':
        return isOpenNow(lounge.hours);
      case 'fullBar':
        return lounge.amenities.fullBar;
      case 'food':
        return lounge.amenities.food;
      case 'lockers':
        return lounge.amenities.lockers;
      default:
        return true;
    }
  });

  // Sort lounges
  const sortedLounges = [...filteredLounges].sort((a, b) => {
    switch (sortBy) {
      case 'distance':
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  // Map functionality is disabled - using static Google Maps link instead
  // To enable interactive maps, add a Mapbox token in environment config

  const filters: { key: FilterOption; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'All', icon: null },
    { key: 'open', label: 'Open Now', icon: <Clock className="w-3 h-3" /> },
    { key: 'fullBar', label: 'Full Bar', icon: <Wine className="w-3 h-3" /> },
    { key: 'food', label: 'Food', icon: <UtensilsCrossed className="w-3 h-3" /> },
    { key: 'lockers', label: 'Lockers', icon: <Lock className="w-3 h-3" /> },
  ];

  return (
    <div className="flex flex-col h-full bg-stone-900">
      {/* Header */}
      <div className="p-4 border-b border-stone-800">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-white">Find a Lounge</h1>
          {!userLocation && !locationLoading && (
            <button
              onClick={onRequestLocation}
              className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-stone-900 rounded-lg text-sm font-medium"
            >
              <Navigation className="w-4 h-4" />
              Use My Location
            </button>
          )}
          {locationLoading && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-800 text-stone-300 rounded-lg text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Getting location...
            </div>
          )}
          {userLocation && (
            <div className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm">
              <Check className="w-4 h-4" />
              Location found
            </div>
          )}
        </div>

        {/* Location error message */}
        {locationError && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {locationError}
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input
            type="text"
            placeholder="Search city, zip, or lounge name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-20 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-amber-500"
          />
          {searchQuery && isLocationSearch(searchQuery) && (
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-amber-500 text-stone-900 rounded text-xs font-medium"
            >
              Search
            </button>
          )}
        </form>

        {/* Radius Slider */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-stone-400">Search Radius</span>
            <span className="text-xs font-medium text-amber-500">{searchRadius} miles</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max={RADIUS_OPTIONS.length - 1}
              value={RADIUS_OPTIONS.indexOf(searchRadius)}
              onChange={(e) => onRadiusChange(RADIUS_OPTIONS[parseInt(e.target.value)])}
              className="w-full h-2 bg-stone-700 rounded-lg appearance-none cursor-pointer slider-amber"
            />
            <div className="flex justify-between mt-1">
              {RADIUS_OPTIONS.map((r) => (
                <span
                  key={r}
                  className={`text-xs ${searchRadius === r ? 'text-amber-500' : 'text-stone-500'}`}
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                activeFilter === filter.key
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
            >
              {filter.icon}
              {filter.label}
            </button>
          ))}
        </div>

        {/* City Guides Banner */}
        {onNavigate && (
          <button
            onClick={() => onNavigate('city-guide')}
            className="mt-3 w-full flex items-center justify-between p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl hover:from-amber-500/20 hover:to-orange-500/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <Compass className="w-4 h-4 text-stone-900" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-sm">Explore City Guides</p>
                <p className="text-white/50 text-xs">Ybor City: lounges, history & more</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-500" />
          </button>
        )}
      </div>

      {/* Map/List Toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-stone-850 border-b border-stone-800">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              viewMode === 'map'
                ? 'bg-amber-500 text-stone-900'
                : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
            }`}
          >
            <Map className="w-4 h-4" />
            Map
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              viewMode === 'list'
                ? 'bg-amber-500 text-stone-900'
                : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
        <span className="text-sm text-stone-400">
          {sortedLounges.length} lounge{sortedLounges.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Interactive Google Map */}
      {viewMode === 'map' && (
        <GoogleMap
          lounges={sortedLounges}
          userLocation={userLocation}
          onSelectLounge={(lounge) => {
            setSelectedMapLounge(lounge.id);
            onSelectLounge(lounge);
          }}
          selectedLoungeId={selectedMapLounge}
        />
      )}

      {/* Sort options - only show in list view */}
      {viewMode === 'list' && (
        <div className="flex items-center justify-end px-4 py-2 bg-stone-850 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="bg-stone-800 text-stone-300 text-sm rounded px-2 py-1 border border-stone-700 focus:outline-none focus:border-amber-500"
            >
              <option value="distance">Distance</option>
              <option value="rating">Rating</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      )}

      {/* Lounge list */}
      <div className="flex-1 overflow-y-auto">
        {sortedLounges.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-stone-500 p-8">
            <MapPin className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-center">No lounges found matching your criteria</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveFilter('all');
              }}
              className="mt-4 text-amber-500 text-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-stone-800">
            {sortedLounges.map((lounge) => (
              <LoungeCard
                key={lounge.id}
                lounge={lounge}
                distance={lounge.distance}
                onClick={() => onSelectLounge(lounge)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Lounge card component
interface LoungeCardProps {
  lounge: Lounge & { distance: number | null };
  distance: number | null;
  onClick: () => void;
}

const LoungeCard: React.FC<LoungeCardProps> = ({ lounge, distance, onClick }) => {
  const open = isOpenNow(lounge.hours);

  return (
    <button
      onClick={onClick}
      className="w-full p-4 text-left hover:bg-stone-800/50 transition-colors"
    >
      <div className="flex gap-3">
        {/* Thumbnail placeholder */}
        <div className="w-16 h-16 bg-stone-800 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
          🚬
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-white truncate">{lounge.name}</h3>
              <p className="text-sm text-stone-400">
                {lounge.city}, {lounge.state}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-stone-600 flex-shrink-0" />
          </div>

          <div className="flex items-center gap-3 mt-1.5">
            {/* Rating */}
            {lounge.rating && (
              <div className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span className="text-sm text-stone-300">{lounge.rating}</span>
                {lounge.reviewCount && (
                  <span className="text-xs text-stone-500">({lounge.reviewCount})</span>
                )}
              </div>
            )}

            {/* Distance */}
            {distance !== null && (
              <div className="flex items-center gap-1 text-stone-400">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-sm">{distance.toFixed(1)} mi</span>
              </div>
            )}

            {/* Open status */}
            <div
              className={`flex items-center gap-1 text-sm ${
                open ? 'text-emerald-500' : 'text-stone-500'
              }`}
            >
              {open ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Open
                </>
              ) : (
                <>
                  <X className="w-3.5 h-3.5" />
                  Closed
                </>
              )}
            </div>
          </div>

          {/* Quick amenities */}
          <div className="flex gap-2 mt-2">
            {lounge.amenities.fullBar && (
              <span className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">
                Full Bar
              </span>
            )}
            {lounge.amenities.food && (
              <span className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">
                Food
              </span>
            )}
            {lounge.amenities.lockers && (
              <span className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">
                Lockers
              </span>
            )}
            {lounge.featured && (
              <span className="px-2 py-0.5 bg-amber-500/20 rounded text-xs text-amber-500">
                Featured
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default LoungeFinderView;
